/* ──────────────────────────────────────────────────────────────
   AIWorkplaceWelcome.jsx  ·  AI Workplace profile completion wizard
   ──────────────────────────────────────────────────────────── */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { auth } from "../../../firebase";
import { getFunctions, httpsCallable } from "firebase/functions";
import LoadingCircleSmall from "../../utils/LoadingCircleSmall";
import tpcLogo from "../../../images/TPC-logo-light.png";
import ThursdayCalendar from "./ThursdayCalendar";
import AIWorkplaceCongratulations from "./AIWorkplaceCongratulations";
import { sendApplicationConfirmationEmail } from "./aiWorkplaceEmailService";
import "./AIWorkplaceWelcome.css";

/* ─── static config & lookup tables ─────────────────────────── */
const CONFIG = {
  company: "TPC_Group",
  collectionBrand: "TechPerformanceCentre",
  systemName: "AIWorkplaceUsers",
  completeState: "AI Workplace User",
  learnRoute: "/ai-workplace-dashboard",
};

const AI_EXPERIENCE_LEVELS = [
  "Beginner - No experience with AI tools",
  "Basic - Some experience with AI assistants (ChatGPT, etc.)",
  "Intermediate - Regular use of AI tools for work tasks",
  "Advanced - Proficient with multiple AI platforms and APIs",
  "Expert - AI implementation and strategy experience",
];

const AI_TOOLS = [
  "ChatGPT/OpenAI",
  "Microsoft Copilot",
  "Google Bard/Gemini",
  "Claude (Anthropic)",
  "GitHub Copilot",
  "Notion AI",
  "Jasper AI",
  "Copy.ai",
  "Midjourney",
  "DALL-E",
  "Grok",
  "Other AI tools",
];

const WORKPLACE_SECTORS = [
  "Technology/Software",
  "Healthcare",
  "Finance/Banking",
  "Education",
  "Marketing/Advertising",
  "Manufacturing",
  "Retail/E-commerce",
  "Consulting",
  "Government",
  "Non-profit",
  "Other",
];

const AI_GOALS = [
  "Improve productivity and efficiency",
  "Automate repetitive tasks",
  "Enhance decision making",
  "Improve customer service",
  "Develop new products/services",
  "Data analysis and insights",
  "Content creation",
  "Learning and skill development",
  "Other",
];

const AGE_BRACKETS = [
  "18 – 24",
  "25 – 34",
  "35 – 44",
  "45 – 54",
  "55 – 64",
  "65+",
];

const GENDERS = ["Male", "Female", "Non-binary", "Prefer not to say"];

// Countries will be fetched from API

const EMPLOYMENT_STATUS = [
  "Employed – Full-time",
  "Employed – Part-time",
  "Self-employed",
  "Unemployed",
  "Student",
  "Retired",
  "Other",
];

const APPLICATION_TYPES = ["Individual", "Team"];

const blankProfile = {
  // Basic info (from registration)
  name: "",
  email: "",
  company: "",

  // Personal information
  fullName: "",
  surname: "",
  phone: "",
  cellPhone: "",
  countryCode: "+27",
  country: "South Africa",
  homeTown: "",
  ageBracket: "",
  gender: "",
  employmentStatus: "",

  // AI-specific information
  aiExperienceLevel: "",
  currentAITools: [],
  workplaceSector: "",
  applicationType: "", // "Individual" or "Team"
  teamMemberCount: "",
  aiGoals: [],
  aiChallenges: "",
  selectedThursdays: [],

  // System fields
  userType: "AI Workplace User",
  state: "AI Workplace User",
  status: "completed",
  isCompleted: true,
  id: "",
  uid: "",
  dateAdded: "",
  lastActive: "",
  lastUpdated: "",
};

export default function AIWorkplaceWelcome() {
  /* auth / Redux --------------------------------------------- */
  const reduxUser = useSelector((s) => s.user);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  /* wizard state --------------------------------------------- */
  const [step, setStep] = useState(1); // 1-4 forms
  const [profile, setProfile] = useState(blankProfile);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [done, setDone] = useState(false);
  const [showCongratulations, setShowCongratulations] = useState(false);
  const [completedProfile, setCompletedProfile] = useState(null);
  const [countries, setCountries] = useState([]);

  const nav = useNavigate();

  /*──────────────── fetch countries from API ─────────────────*/
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await fetch("https://restcountries.com/v3.1/all");

        if (!response.ok) {
          throw new Error("Failed to fetch countries from API");
        }

        const data = await response.json();

        // Map the data to our format with flags
        const countriesList = data
          .map((country) => ({
            value: country.name.common,
            label: `${country.flag} ${country.name.common}`,
          }))
          .sort((a, b) => a.value.localeCompare(b.value));

        setCountries(countriesList);
      } catch (error) {
        console.error("Error fetching countries:", error);
        // Fallback to hardcoded countries if API fails
        setCountries([
          { value: "South Africa", label: "🇿🇦 South Africa" },
          { value: "United States", label: "🇺🇸 United States" },
          { value: "United Kingdom", label: "🇬🇧 United Kingdom" },
          { value: "Canada", label: "🇨🇦 Canada" },
          { value: "Australia", label: "🇦🇺 Australia" },
          { value: "Germany", label: "🇩🇪 Germany" },
          { value: "France", label: "🇫🇷 France" },
          { value: "India", label: "🇮🇳 India" },
          { value: "Nigeria", label: "🇳🇬 Nigeria" },
          { value: "Brazil", label: "🇧🇷 Brazil" },
        ]);
      }
    };

    fetchCountries();
  }, []);

  /*──────────────── auth check ───────────────────────────────*/
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user && user.emailVerified) {
        setCurrentUser(user);
        // Pre-populate basic info from registration
        setProfile((prev) => ({
          ...prev,
          name: reduxUser?.name || "",
          fullName: reduxUser?.name || "",
          email: user.email,
          company: reduxUser?.company || "",
          id: user.email,
          uid: user.uid,
        }));
      } else {
        nav("/ai-workplace-login");
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [nav, reduxUser]);

  /*──────────────── step navigation ──────────────────────────*/
  const next = () => {
    if (step < 4) {
      setStep(step + 1);
    }
  };

  const back = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  /*──────────────── field updates ────────────────────────────*/
  const updateField = (field, value) => {
    setProfile((prev) => ({ ...prev, [field]: value }));
  };

  const updateArrayField = (field, value, checked) => {
    setProfile((prev) => ({
      ...prev,
      [field]: checked
        ? [...(prev[field] || []), value]
        : (prev[field] || []).filter((item) => item !== value),
    }));
  };

  /*──────────────── save to Firestore ────────────────────────*/
  const saveToFirestore = async (markCompleted = false) => {
    if (!currentUser) return;

    setBusy(true);
    setError("");

    try {
      const fn = getFunctions();
      const fsOp = httpsCallable(fn, "firestoreOperations");

      const updates = {
        ...profile,
        ...(markCompleted
          ? {
              // System fields - always set these
              state: CONFIG.completeState,
              userType: "AI Workplace User",
              id: currentUser.email,
              email: currentUser.email,
              dateAdded: new Date().toLocaleDateString("en-ZA"),
              lastActive: new Date().toLocaleDateString("en-ZA"),
              lastUpdated: new Date().toISOString(),

              // Always set as completed when user clicks Finish
              isCompleted: true,
              status: "completed",

              // Preserve user data - only include fields that have values
              ...(profile.name && { name: profile.name }),
              ...(profile.fullName && { fullName: profile.fullName }),
              ...(profile.surname && { surname: profile.surname }),
              ...(profile.phone && { phone: profile.phone }),
              ...(profile.cellPhone && { cellPhone: profile.cellPhone }),
              ...(profile.countryCode && { countryCode: profile.countryCode }),
              ...(profile.country && { country: profile.country }),
              ...(profile.homeTown && { homeTown: profile.homeTown }),
              ...(profile.ageBracket && { ageBracket: profile.ageBracket }),
              ...(profile.gender && { gender: profile.gender }),
              ...(profile.employmentStatus && {
                employmentStatus: profile.employmentStatus,
              }),
              ...(profile.company && { company: profile.company }),
              ...(profile.aiExperienceLevel && {
                aiExperienceLevel: profile.aiExperienceLevel,
              }),
              ...(profile.currentAITools?.length > 0 && {
                currentAITools: profile.currentAITools,
              }),
              ...(profile.workplaceSector && {
                workplaceSector: profile.workplaceSector,
              }),
              ...(profile.applicationType && {
                applicationType: profile.applicationType,
              }),
              ...(profile.teamMemberCount && {
                teamMemberCount: profile.teamMemberCount,
              }),
              ...(profile.aiGoals?.length > 0 && { aiGoals: profile.aiGoals }),
              ...(profile.aiChallenges && {
                aiChallenges: profile.aiChallenges,
              }),
              ...(profile.selectedThursdays?.length > 0 && {
                selectedThursdays: profile.selectedThursdays,
              }),
            }
          : {}),
      };

      await fsOp({
        operation: "write",
        company: CONFIG.company,
        collectionBrand: CONFIG.collectionBrand,
        systemName: CONFIG.systemName,
        uniqueId: currentUser.email,
        documentData: updates,
      });

      console.log("[AIW] Profile saved to Firestore:", updates);

      // If there's a selected intake date, save it to ai-intakes collection
      if (
        markCompleted &&
        updates.selectedThursdays &&
        updates.selectedThursdays.length > 0
      ) {
        try {
          await fsOp({
            operation: "write",
            company: CONFIG.company,
            collectionBrand: CONFIG.collectionBrand,
            systemName: "ai-intakes",
            uniqueId: `${currentUser.email}_${updates.selectedThursdays[0]}`,
            documentData: {
              intakeDate: updates.selectedThursdays[0],
              email: currentUser.email,
              name: updates.fullName || updates.name,
              company: updates.company,
              applicationType: updates.applicationType,
              teamMemberCount: updates.teamMemberCount,
              aiExperienceLevel: updates.aiExperienceLevel,
              workplaceSector: updates.workplaceSector,
              dateBooked: new Date().toISOString(),
              status: "confirmed",
            },
          });
          console.log("[AIW] Intake booking saved successfully");
        } catch (intakeError) {
          console.error("[AIW] Error saving intake booking:", intakeError);
          // Continue even if intake booking fails
        }
      }

      if (markCompleted) {
        setSuccess("Profile completed successfully!");
        setDone(true);
        setCompletedProfile(updates);

        // Send confirmation email
        try {
          const emailResult = await sendApplicationConfirmationEmail(updates);
          if (emailResult.success) {
            console.log("Application confirmation email sent successfully");
          } else {
            console.warn(
              "Failed to send confirmation email:",
              emailResult.error
            );
          }
        } catch (emailError) {
          console.warn("Error sending confirmation email:", emailError);
        }

        // Show congratulations page
        setShowCongratulations(true);
      }
    } catch (err) {
      console.error("[AIW] Save error:", err);
      setError(err.message || "Failed to save profile");
    } finally {
      setBusy(false);
    }
  };

  /*──────────────── finish handler ───────────────────────────*/
  const finish = async () => {
    await saveToFirestore(true);
  };

  /*──────────────── render step content ──────────────────────*/
  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="aiw-step">
            <h2>Personal Information</h2>

            <div className="aiw-form-row">
              <div className="aiw-form-group">
                <label>Full Name *</label>
                <input
                  type="text"
                  value={profile.fullName}
                  onChange={(e) => {
                    updateField("fullName", e.target.value);
                    updateField("name", e.target.value);
                  }}
                  className="aiw-input"
                  placeholder="Enter your full name"
                  required
                />
              </div>

              <div className="aiw-form-group">
                <label>Email Address *</label>
                <input
                  type="email"
                  value={profile.email}
                  onChange={(e) => updateField("email", e.target.value)}
                  className="aiw-input"
                  placeholder="Enter your email address"
                  required
                  disabled
                />
                <small className="aiw-help-text-yellow">
                  Email cannot be changed
                </small>
              </div>
            </div>

            <div className="aiw-form-row">
              <div className="aiw-form-group">
                <label>Phone Number *</label>
                <input
                  type="tel"
                  value={profile.phone}
                  onChange={(e) => {
                    // Allow only numbers, spaces, hyphens, plus, and parentheses
                    const value = e.target.value.replace(/[^0-9+\-\s()]/g, "");
                    updateField("phone", value);
                  }}
                  onKeyPress={(e) => {
                    // Prevent any non-numeric keys except special phone characters
                    if (!/[0-9+\-\s()]/.test(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  className="aiw-input"
                  placeholder="e.g., +27 82 123 4567"
                  pattern="[0-9+\-\s()]+"
                  title="Please enter a valid phone number (numbers only)"
                  required
                />
                <small
                  className="aiw-help-text"
                  style={{
                    color: "#666",
                    fontSize: "12px",
                    marginTop: "4px",
                    display: "block",
                  }}
                >
                  Enter numbers only (e.g., +27821234567 or 082 123 4567)
                </small>
              </div>

              <div className="aiw-form-group">
                <label>Country *</label>
                <select
                  value={profile.country}
                  onChange={(e) => updateField("country", e.target.value)}
                  className="aiw-select"
                  required
                >
                  <option value="">Select your country</option>
                  {countries.map((country, index) => (
                    <option key={index} value={country.value}>
                      {country.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="aiw-form-row">
              <div className="aiw-form-group">
                <label>Age Bracket *</label>
                <select
                  value={profile.ageBracket}
                  onChange={(e) => updateField("ageBracket", e.target.value)}
                  className="aiw-select"
                  required
                >
                  <option value="">Select your age bracket</option>
                  {AGE_BRACKETS.map((age, index) => (
                    <option key={index} value={age}>
                      {age}
                    </option>
                  ))}
                </select>
              </div>

              <div className="aiw-form-group">
                <label>Gender *</label>
                <select
                  value={profile.gender}
                  onChange={(e) => updateField("gender", e.target.value)}
                  className="aiw-select"
                  required
                >
                  <option value="">Select your gender</option>
                  {GENDERS.map((gender, index) => (
                    <option key={index} value={gender}>
                      {gender}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="aiw-form-group">
              <label>Company/Organization *</label>
              <input
                type="text"
                value={profile.company}
                onChange={(e) => updateField("company", e.target.value)}
                className="aiw-input"
                placeholder="Enter your company name"
                required
              />
            </div>

            <div className="aiw-form-group">
              <label>Employment Status *</label>
              <select
                value={profile.employmentStatus}
                onChange={(e) =>
                  updateField("employmentStatus", e.target.value)
                }
                className="aiw-select"
                required
              >
                <option value="">Select your employment status</option>
                {EMPLOYMENT_STATUS.map((status, index) => (
                  <option key={index} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="aiw-step">
            <h2>AI Experience & Application Type</h2>

            <div className="aiw-form-row">
              <div className="aiw-form-group">
                <label>Are you applying as an individual or team? *</label>
                <select
                  value={profile.applicationType}
                  onChange={(e) =>
                    updateField("applicationType", e.target.value)
                  }
                  className="aiw-select"
                  required
                >
                  <option value="">Select application type</option>
                  {APPLICATION_TYPES.map((type, index) => (
                    <option key={index} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>

              {profile.applicationType === "Team" && (
                <div className="aiw-form-group">
                  <label>How many team members? *</label>
                  <input
                    type="number"
                    value={profile.teamMemberCount}
                    onChange={(e) =>
                      updateField("teamMemberCount", e.target.value)
                    }
                    className="aiw-input"
                    placeholder="Enter number of team members"
                    min="2"
                    max="999"
                    required
                  />
                </div>
              )}
            </div>

            <div className="aiw-form-group">
              <label>What's your current AI experience level?</label>
              <select
                value={profile.aiExperienceLevel}
                onChange={(e) =>
                  updateField("aiExperienceLevel", e.target.value)
                }
                className="aiw-select"
              >
                <option value="">Select your experience level</option>
                {AI_EXPERIENCE_LEVELS.map((level, index) => (
                  <option key={index} value={level}>
                    {level}
                  </option>
                ))}
              </select>
            </div>

            <div className="aiw-form-group">
              <label>What sector does your company operate in?</label>
              <select
                value={profile.workplaceSector}
                onChange={(e) => updateField("workplaceSector", e.target.value)}
                className="aiw-select"
              >
                <option value="">Select your sector</option>
                {WORKPLACE_SECTORS.map((sector, index) => (
                  <option key={index} value={sector}>
                    {sector}
                  </option>
                ))}
              </select>
            </div>

            <div className="aiw-form-group">
              <label>
                Which AI tools do you currently use? (Select all that apply)
              </label>
              <div className="aiw-checkbox-group">
                {AI_TOOLS.map((tool, index) => (
                  <label key={index} className="aiw-checkbox-label">
                    <input
                      type="checkbox"
                      checked={profile.currentAITools?.includes(tool) || false}
                      onChange={(e) =>
                        updateArrayField(
                          "currentAITools",
                          tool,
                          e.target.checked
                        )
                      }
                    />
                    {tool}
                  </label>
                ))}
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="aiw-step">
            <h2>AI Goals & Challenges</h2>
            <div className="aiw-form-group">
              <label>
                What are your main AI goals? (Select all that apply)
              </label>
              <div className="aiw-checkbox-group">
                {AI_GOALS.map((goal, index) => (
                  <label key={index} className="aiw-checkbox-label">
                    <input
                      type="checkbox"
                      checked={profile.aiGoals?.includes(goal) || false}
                      onChange={(e) =>
                        updateArrayField("aiGoals", goal, e.target.checked)
                      }
                    />
                    {goal}
                  </label>
                ))}
              </div>
            </div>

            <div className="aiw-form-group">
              <label>
                What are your biggest challenges with AI implementation?
              </label>
              <textarea
                value={profile.aiChallenges}
                onChange={(e) => updateField("aiChallenges", e.target.value)}
                placeholder="Describe any challenges you face with AI adoption, training, or implementation..."
                className="aiw-textarea"
                rows="4"
              />
            </div>
          </div>
        );

      case 4:
        return (
          <div className="aiw-step">
            <h2>Implementation & Training</h2>

            <div className="aiw-form-group">
              <ThursdayCalendar
                selectedThursdays={profile.selectedThursdays || []}
                onSelectionChange={(selectedDates) =>
                  updateField("selectedThursdays", selectedDates)
                }
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return <LoadingCircleSmall label="Loading..." />;
  }

  if (!currentUser) {
    return null;
  }

  // Show congratulations page if completed
  if (showCongratulations && completedProfile) {
    return <AIWorkplaceCongratulations profileData={completedProfile} />;
  }

  return (
    <div className="aiw-welcome-container">
      <div className="aiw-welcome-header">
        <img src={tpcLogo} alt="TPC" className="aiw-welcome-logo" />
        <h1>Complete Your AI Workplace Profile</h1>
        <p>Help us understand your AI needs and goals</p>
      </div>

      <div className="aiw-welcome-content">
        <div className="aiw-progress-bar">
          <div
            className="aiw-progress-fill"
            style={{ width: `${(step / 4) * 100}%` }}
          />
        </div>

        <div className="aiw-step-indicator">Step {step} of 4</div>

        {error && <div className="aiw-error">{error}</div>}
        {success && <div className="aiw-success">{success}</div>}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (step === 4) {
              finish();
            } else {
              next();
            }
          }}
        >
          {renderStep()}

          {step >= 1 && step <= 4 && (
            <div className="aiw-navigation">
              {step > 1 && (
                <button
                  type="button"
                  onClick={back}
                  className="aiw-btn aiw-btn-secondary"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M19 12H5M12 19l-7-7 7-7"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  Back
                </button>
              )}

              {step < 4 ? (
                <button type="submit" className="aiw-btn aiw-btn-primary">
                  Next
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M5 12h14M12 5l7 7-7 7"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              ) : (
                <button
                  type="submit"
                  className="aiw-btn aiw-btn-primary"
                  disabled={busy}
                >
                  {busy ? "Completing Profile..." : "Complete Profile"}
                </button>
              )}
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
